import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { Collapse } from 'antd';
import moment from 'moment';
import { getQuestionnaireVersionLabel } from './questionnaire-version';
const { Panel } = Collapse;

interface Props {
    jobs: any[];
    userID: number;
    submissions: any[];
}

function UserSublist(props: Props) {
    interface ColumnType {
        key: React.Key;
        job: any;
        task: number;
        phase: string;
    }

    interface HistoryColumns {
        key: React.Key;
        job: number;
        phase: string;
        time: string;
        questionnaireVersion: string;
    }

    const history = useHistory();
    const [data, setData] = useState<ColumnType[]>([]);
    const [userHistory, setUserHistory] = useState<any[]>([]);
    const columns: TableColumnsType<ColumnType> = [
        {
            title: 'Job',
            dataIndex: 'job',
            key: 'job',
            render(job: any): JSX.Element {
                return (
                    <div>
                        <Button
                            className='cvat-open-job-button'
                            type='link'
                            onClick={(e: React.MouseEvent): void => {
                                e.preventDefault();
                                history.push(`/tasks/${job.taskId}/jobs/${job.id}`);
                            }}
                            href={`/tasks/${job.taskId}/jobs/${job.id}`}
                        >
                            {`#${job.id}`}
                        </Button>
                    </div>
                );
            },
        },
        {
            title: 'Task',
            dataIndex: 'task',
            key: 'task',
            render(task: number): JSX.Element {
                return (
                    <div>
                        <Button
                            className='cvat-open-task-button'
                            type='link'
                            onClick={(e: React.MouseEvent): void => {
                                e.preventDefault();
                                history.push(`/tasks/${task}`);
                            }}
                            href={`/tasks/${task}`}
                        >
                            {`#${task}`}
                        </Button>
                    </div>
                );
            },
        },
        {
            title: 'Phase',
            dataIndex: 'phase',
            key: 'phase',
            filters: [
                { text: 'phase0', value: 'phase0' },
                { text: 'phase1a', value: 'phase1a' },
                { text: 'phase1b', value: 'phase1b' },
                { text: 'phase2a', value: 'phase2a' },
                { text: 'phase2b', value: 'phase2b' },
                { text: 'phase3a', value: 'phase3a' },
                { text: 'phase3b', value: 'phase3b' },
                { text: 'phase4a', value: 'phase4a' },
                { text: 'phase4b', value: 'phase4b' },
            ],
            onFilter: (value: string | number | boolean, record: any) => record.phase === value,
        },
        {
            title: 'state',
            dataIndex: 'state',
            key: 'state',
            filters: [
                { text: 'new', value: 'new' },
                { text: 'in progress', value: 'in progress' },
            ],
            onFilter: (value: string | number | boolean, record: any) => record.state === value,
            render: (state: string, record: any): JSX.Element => {
                if (state === 'in progress') {
                    return <span>{`In progress (${record.job.progress || 0}%)`}</span>;
                }
                if (state === 'completed' && record.phase === 'phase3b') {
                    return <span>Submitted</span>;
                }
                return <span>{state.charAt(0).toUpperCase() + state.slice(1)}</span>;
            },
        },
    ];

    const historyColumns: TableColumnsType<HistoryColumns> = [
        {
            title: 'Job',
            dataIndex: 'job',
            key: 'job',
            render(job: number): JSX.Element {
                return (
                    <div>
                        <Button
                            className='cvat-open-job-button'
                            type='link'
                            // onClick={(e: React.MouseEvent): void => {
                            //     e.preventDefault();
                            //     history.push(`/tasks/${job}`);
                            // }}
                            // href={`/tasks/${job}`}
                        >
                            {`#${job}`}
                        </Button>
                    </div>
                );
            },
            sorter: (a: HistoryColumns, b: HistoryColumns) => a.job - b.job,
        },
        {
            title: 'Phase',
            dataIndex: 'phase',
            key: 'phase',
            filters: [
                { text: 'phase0', value: 'phase0' },
                { text: 'phase1a', value: 'phase1a' },
                { text: 'phase1b', value: 'phase1b' },
                { text: 'phase2a', value: 'phase2a' },
                { text: 'phase2b', value: 'phase2b' },
                { text: 'phase3a', value: 'phase3a' },
                { text: 'phase3b', value: 'phase3b' },
                { text: 'phase4a', value: 'phase4a' },
                { text: 'phase4b', value: 'phase4b' },
            ],
            onFilter: (value: string | number | boolean, record: any) => record.phase === value,
        },
        {
            title: 'Submission Time',
            dataIndex: 'time',
            key: 'time',
            render(time: string): JSX.Element {
                const [date, timestamp] = time.split('T');
                return (
                    <div>
                        {date} {timestamp.slice(0, 5)}
                    </div>
                );
            },
            sorter: (a: HistoryColumns, b: HistoryColumns) => moment(a.time).unix() - moment(b.time).unix(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Questionnaire Version',
            dataIndex: 'questionnaireVersion',
            key: 'questionnaireVersion',
        },
    ];

    useEffect(() => {
        const jobs = props.jobs;
        const userID = props.userID;
        const submissions = props.submissions;
        const filteredJobs = jobs.filter((job: any) => (job.assignee ? job.assignee.id === userID : false));
        const jobsData = filteredJobs.map((job: any) => {
            return {
                key: job.id,
                job: job,
                task: job.taskId,
                phase: job.phase,
                state: job.state,
            };
        });
        setData(jobsData);

        const userSubmissions = submissions.filter((submission) => submission.user_id === userID);
        const uniqueUserSubmissions = userSubmissions.filter(
            (submission, index, self) =>
                index === self.findIndex((t) => t.job_id === submission.job_id && t.phase === submission.phase),
        );
        const submissionsData = uniqueUserSubmissions.map((submission: any) => {
            return {
                key: submission.id,
                job: submission.job_id,
                phase: submission.phase,
                time: submission.time,
                questionnaireVersion: getQuestionnaireVersionLabel(submission.questionnaire_version),
            };
        });

        setUserHistory(submissionsData);
    }, []);
    return (
        <div>
            <Collapse defaultActiveKey={['1']}>
                <Panel header={`Active Tasks - ${data.length} Jobs`} key='1'>
                    <Table
                        className='cvat-task-jobs-table'
                        rowClassName={() => 'cvat-task-jobs-table-row'}
                        columns={columns}
                        dataSource={data}
                        size='small'
                    />
                </Panel>
                <Panel header={`Task History - ${userHistory.length} Submissions`} key='2'>
                    <Table
                        className='cvat-task-jobs-table'
                        rowClassName={() => 'cvat-task-jobs-table-row'}
                        columns={historyColumns}
                        dataSource={userHistory}
                        size='small'
                    />
                </Panel>
            </Collapse>
        </div>
    );
}

export default React.memo(UserSublist);
